import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHead } from '@/admin-area/ui/PageHead';
import { PhonePrimary } from '@/admin-area/ui/PhonePrimary';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet';
import { EXPENSES_MANAGE, holds, useExpensesPermissions } from '../use-expenses-permissions';
import { listVehicles, patchVehicle, type ListedVehicle } from './api';
import { useLoaded } from './use-loaded';
import { VehicleForm } from './VehicleForm';

/** The person's vehicles (release 3): add, edit, archive; Gestione also searches the ACI table. */
export function VehiclesPage() {
  const { t } = useTranslation('expenses');
  const searchAci = holds(useExpensesPermissions(), EXPENSES_MANAGE);
  const { state, reload } = useLoaded(() => listVehicles());
  const [editing, setEditing] = useState<ListedVehicle | 'new' | null>(null);
  const [archiveFailed, setArchiveFailed] = useState(false);

  const archive = async (vehicle: ListedVehicle): Promise<void> => {
    setArchiveFailed(false);
    const outcome = await patchVehicle(vehicle.id, { active: false });
    if (outcome.ok) reload();
    else setArchiveFailed(true);
  };
  const add = (
    <Button type="button" onClick={() => setEditing('new')}>
      <Plus />
      {t('mileage.vehicles.add')}
    </Button>
  );
  return (
    <section aria-label={t('mileage.vehicles.title')} className="flex flex-col gap-4">
      <PageHead
        title={t('mileage.vehicles.title')}
        summary={t('mileage.vehicles.summary')}
        actions={<span className="page-primary">{add}</span>}
      />
      {archiveFailed ? <p role="alert">{t('mileage.vehicles.archiveFailed')}</p> : null}
      {state.status === 'loading' ? <p role="status">{t('mileage.loading')}</p> : null}
      {state.status === 'failed' ? <p role="alert">{t('mileage.loadFailed')}</p> : null}
      {state.status === 'ready' && state.items.length === 0 ? (
        <p className="text-muted-foreground">{t('mileage.vehicles.empty')}</p>
      ) : null}
      {state.status === 'ready' ? (
        <ul aria-label={t('mileage.vehicles.title')} className="flex flex-col gap-2">
          {state.items.map((vehicle) => (
            <VehicleRow
              key={vehicle.id}
              vehicle={vehicle}
              onEdit={() => setEditing(vehicle)}
              onArchive={() => void archive(vehicle)}
            />
          ))}
        </ul>
      ) : null}
      <PhonePrimary className="mt-4">{add}</PhonePrimary>
      <Sheet open={editing !== null} onOpenChange={(open) => (open ? undefined : setEditing(null))}>
        <SheetContent className="flex flex-col gap-4 p-4">
          <SheetTitle>
            {editing === 'new' ? t('mileage.vehicles.addTitle') : t('mileage.vehicles.editTitle')}
          </SheetTitle>
          <SheetDescription>{t('mileage.vehicles.addHelp')}</SheetDescription>
          {editing === null ? null : (
            <VehicleForm
              {...(editing === 'new' ? {} : { vehicle: editing })}
              searchAci={searchAci}
              onCancel={() => setEditing(null)}
              onSaved={() => {
                setEditing(null);
                reload();
              }}
            />
          )}
        </SheetContent>
      </Sheet>
    </section>
  );
}

function VehicleRow({
  vehicle,
  onEdit,
  onArchive,
}: {
  readonly vehicle: ListedVehicle;
  readonly onEdit: () => void;
  readonly onArchive: () => void;
}) {
  const { t } = useTranslation('expenses');
  const facts = [
    vehicle.plate,
    vehicle.fuel
      ? t(`mileage.vehicles.fuels.${vehicle.fuel}`, { defaultValue: vehicle.fuel })
      : null,
    vehicle.aciVehicleRef ? `ACI ${vehicle.aciVehicleRef}` : t('mileage.vehicles.noAci'),
  ].filter(Boolean);
  return (
    <li className="border-border bg-card flex flex-wrap items-center justify-between gap-3 rounded-xl border p-3">
      <span className="flex min-w-0 flex-col">
        <b className="truncate">{vehicle.label}</b>
        <small className="text-muted-foreground">{facts.join(' · ')}</small>
      </span>
      <span className="flex gap-1">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="max-lg:min-h-11"
          onClick={onEdit}
        >
          {t('mileage.edit')}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="max-lg:min-h-11"
          onClick={onArchive}
        >
          {t('mileage.vehicles.archive')}
        </Button>
      </span>
    </li>
  );
}
